// src/ui/hud.ts
// DOM chrome wiring (spec §5.1, §5.6): the top bar (settings gear · hint · "Level N" · star), the big
// bottom Undo/Reset buttons, an optional Next affordance on win, a subtle Hint button (§4.1), and a
// minimal settings sheet (sound · symbols · reduced motion · reset progress). Pure DOM glue — it owns
// no game logic and holds only its own UI copy of Settings.

import type { ReducedMotionPref, Settings } from '../state/storage';

export interface HudCallbacks {
  onUndo(): void;
  onReset(): void;
  onNext(): void;
  /** Fired when the Hint button is tapped (§4.1). */
  onHint(): void;
  /** Fired whenever any settings control changes; receives the full new Settings. */
  onSettingsChange(settings: Settings): void;
  /** Fired after the user confirms "Reset progress". */
  onResetProgress(): void;
}

function requireEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (el === null) {
    throw new Error(`Hud: missing required element #${id}`);
  }
  return el as T;
}

function isReducedMotionPref(v: string): v is ReducedMotionPref {
  return v === 'auto' || v === 'on' || v === 'off';
}

export class Hud {
  private readonly cb: HudCallbacks;
  private settings: Settings;

  private readonly levelLabel: HTMLElement;
  private readonly movesLabel: HTMLElement;
  private readonly star: HTMLElement;
  private readonly undoBtn: HTMLButtonElement;
  private readonly resetBtn: HTMLButtonElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly gearBtn: HTMLButtonElement;
  private readonly hintBtn: HTMLButtonElement;

  private readonly overlay: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly soundToggle: HTMLInputElement;
  private readonly motionSelect: HTMLSelectElement;
  private readonly resetProgressBtn: HTMLButtonElement;

  constructor(cb: HudCallbacks, initialSettings: Settings) {
    this.cb = cb;
    this.settings = initialSettings;

    this.levelLabel = requireEl('level-label');
    this.movesLabel = requireEl('moves-label');
    this.star = requireEl('star');
    this.undoBtn = requireEl<HTMLButtonElement>('undo-btn');
    this.resetBtn = requireEl<HTMLButtonElement>('reset-btn');
    this.nextBtn = requireEl<HTMLButtonElement>('next-btn');
    this.gearBtn = requireEl<HTMLButtonElement>('gear-btn');
    this.hintBtn = requireEl<HTMLButtonElement>('hint-btn');

    this.overlay = requireEl('settings-overlay');
    this.closeBtn = requireEl<HTMLButtonElement>('settings-close');
    this.soundToggle = requireEl<HTMLInputElement>('toggle-sound');
    this.motionSelect = requireEl<HTMLSelectElement>('select-motion');
    this.resetProgressBtn = requireEl<HTMLButtonElement>('reset-progress-btn');

    this.undoBtn.addEventListener('click', () => this.cb.onUndo());
    this.resetBtn.addEventListener('click', () => this.cb.onReset());
    this.nextBtn.addEventListener('click', () => this.cb.onNext());
    this.hintBtn.addEventListener('click', () => this.cb.onHint());
    this.gearBtn.addEventListener('click', () => this.openSettings());
    this.closeBtn.addEventListener('click', () => this.closeSettings());
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeSettings();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.hidden) {
        this.closeSettings();
      }
    });

    this.soundToggle.addEventListener('change', () => this.pushSettings());
    this.motionSelect.addEventListener('change', () => this.pushSettings());
    this.resetProgressBtn.addEventListener('click', () => {
      const ok = window.confirm('Reset all progress? This starts again from Level 1.');
      if (ok) {
        this.cb.onResetProgress();
        this.closeSettings();
      }
    });

    this.syncControls();
  }

  // ---- top bar ----

  setLevel(level: number): void {
    this.levelLabel.textContent = `Level ${level}`;
  }

  /** Cosmetic par readout (§2.6). */
  setMoves(count: number, par: number): void {
    this.movesLabel.textContent = `${count} / ${par}`;
  }

  /** Lights the top-right star when the run is at/under par. */
  setStar(earned: boolean): void {
    this.star.classList.toggle('earned', earned);
    this.star.setAttribute('aria-label', earned ? 'On track for a star' : 'Over par');
  }

  setUndoEnabled(enabled: boolean): void {
    this.undoBtn.disabled = !enabled;
  }

  showNext(show: boolean): void {
    this.nextBtn.hidden = !show;
    if (show) {
      this.nextBtn.focus({ preventScroll: true });
    }
  }

  // ---- settings sheet ----

  private openSettings(): void {
    this.syncControls();
    this.overlay.hidden = false;
    this.closeBtn.focus({ preventScroll: true });
  }

  private closeSettings(): void {
    this.overlay.hidden = true;
    this.gearBtn.focus({ preventScroll: true });
  }

  private syncControls(): void {
    this.soundToggle.checked = this.settings.sound;
    this.motionSelect.value = this.settings.reducedMotion;
  }

  private pushSettings(): void {
    const motionRaw = this.motionSelect.value;
    const reducedMotion: ReducedMotionPref = isReducedMotionPref(motionRaw)
      ? motionRaw
      : 'auto';
    this.settings = {
      sound: this.soundToggle.checked,
      symbols: this.settings.symbols, // symbols UI removed (color-only bricks); field kept for storage compat
      reducedMotion,
    };
    this.cb.onSettingsChange(this.settings);
  }
}
